import { useEffect } from 'react'
import { useDispatch, useSelector } from '../../Flux/hooks.ts'
import type { SpinMetaQuery } from '../../Flux/Slice/Spin/SpinMetaSlice.ts'
import { spinMetaKey, spinMetaSlice } from '../../Flux/Slice/Spin/SpinMetaSlice.ts'
import type { ISpinStatusEntry } from '../../Flux/Slice/Spin/SpinStatusSlice.ts'
import { spinStatusSlice } from '../../Flux/Slice/Spin/SpinStatusSlice.ts'
import { selectSpinMeta } from '../../Flux/Selector/Spin/SpinMetaSelectors.ts'
import { selectSessionExpired } from '../../Flux/Selector/Authentication/SessionExpiredSelector.ts'
import { selectSpinStatus } from '../../Flux/Selector/Spin/SpinStatusSelectors.ts'
import type { SpinAlgorithmState } from '../../Generated/SpinAlgorithmState.ts'
import type { SpinChangePoint, UnspinnedCount } from '../../Repository/Visualization/SpinMeta.js'

/**
 * The scope's SPIN state while `enabled`: requested on mount — a fetch the first time, an `If-None-Match` revalidation
 * after — and revalidated on window focus while mounted. Every consumer of a scope shares one entry and one request.
 */
export function useSpinState(scopeId: string | null | undefined, enabled: boolean): ISpinStatusEntry {
  const dispatch = useDispatch()
  const active = enabled && scopeId ? scopeId : null
  const entry = useSelector(selectSpinStatus(active))
  const paused = useSelector(selectSessionExpired)
  useEffect(() => {
    if (!active) {
      return
    }
    dispatch(spinStatusSlice.actions.watchSpinState({ scopeId: active }))
    return () => {
      dispatch(spinStatusSlice.actions.unwatchSpinState({ scopeId: active }))
    }
  }, [dispatch, active])
  useEffect(() => {
    if (active && !paused) {
      dispatch(spinStatusSlice.actions.requestSpinState({ scopeId: active }))
    }
  }, [dispatch, active, paused])
  return entry
}

const algorithmState = (entry: ISpinStatusEntry, algorithmType: string): SpinAlgorithmState | undefined =>
  entry.data?.algorithms?.find((a) => a.algorithmType === algorithmType)

/** Algorithms with SPIN coverage for the scope: null while loading or when disabled; `error` once a fetch failed with nothing to show. */
export function useSpinAlgorithms(scopeId: string | null | undefined, enabled: boolean): { algorithms: string[] | null; error: boolean } {
  const entry = useSpinState(scopeId, enabled)
  if (!enabled || !scopeId) {
    return { algorithms: null, error: false }
  }
  const algorithms = entry.data ? (entry.data.algorithms ?? []).map((a) => a.algorithmType) : null
  return { algorithms, error: entry.status === 'error' && algorithms === null }
}

/** The scope's change-points under one algorithm, oldest first; empty while loading, disabled, or after a failure with nothing cached. */
export function useSpinChangePoints(scopeId: string | null | undefined, algorithmType: string, enabled: boolean): SpinChangePoint[] {
  const entry = useSpinState(scopeId, enabled)
  if (!enabled || !scopeId || algorithmType === '') {
    return NO_CHANGE_POINTS
  }
  return algorithmState(entry, algorithmType)?.changePoints ?? NO_CHANGE_POINTS
}

/** Binds an As-of query to the spinMeta cache; a null query neither fetches nor reads. */
const useSpinMetaQuery = (query: SpinMetaQuery | null) => {
  const dispatch = useDispatch()
  const entry = useSelector(selectSpinMeta(query))
  const paused = useSelector(selectSessionExpired)
  const key = query ? spinMetaKey(query) : null
  // Re-requests when a changed scope state turns the entry idle; the epic drops requests for a loading or ready key.
  const stale = entry.status === 'idle'
  useEffect(() => {
    if (query && !paused) {
      dispatch(spinMetaSlice.actions.requestSpinMeta({ query }))
    }
    // `key` is a total function of the query, which is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, key, stale, paused])
  return entry
}

/**
 * How many of the scope's OTUs have no SPIN under the algorithm as of `atTime`; null until known. The latest count
 * (blank `atTime`) comes with the scope's state, an explicit As-of is asked for on its own.
 */
export function useSpinUnspinnedCount(
  scopeId: string | null | undefined,
  algorithmType: string,
  atTime: string,
  enabled: boolean
): UnspinnedCount | null {
  const usable = enabled && !!scopeId && algorithmType !== ''
  const entry = useSpinState(scopeId, usable && atTime === '')
  const asOf = useSpinMetaQuery(usable && atTime !== '' && scopeId ? { kind: 'unspinned', scopeId, algorithmType, atTime } : null)
  if (!usable) {
    return null
  }
  if (atTime === '') {
    return algorithmState(entry, algorithmType)?.unspinned ?? null
  }
  return (asOf.data as UnspinnedCount | null | undefined) ?? null
}

const NO_CHANGE_POINTS: SpinChangePoint[] = []
